import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, PermissionService, ToastService } from '@services/index';
import { Align, Permission } from 'app/enums';
import { AgreementStatus, SortType } from 'app/types';
import { BehaviorSubject, debounceTime, EMPTY, finalize, map, merge, Observable, of, switchMap } from 'rxjs';
import { Filter, FilterForm } from './inventory-list.model';
import { sortStrings } from '@helpers/sort-string-helper';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { APP_ROUTES } from 'app/constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalHelper } from '@helpers/modal-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AgreementRoutes } from '../../agreement.routes';
import { AgreementFilterComponent, TableType } from '../../components/filter/filter.component';
import { FilterConfig, SelectedFilter } from '../../components/filter/filter-model';
import { ChangeResponsbileModalComponent } from '../../components/change-responsible-modal/change-responsible-modal.component';
import {
    GetInventoriesResponse,
    InventoryResponse
} from '@services/api-client/responses/get-inventories-response.type';

@UntilDestroy()
@Component({
    selector: 'app-inventory-list',
    templateUrl: './inventory-list.component.html',
    styleUrl: './inventory-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InventoryListComponent implements OnInit {
    @ViewChild(AgreementFilterComponent) public readonly filterComponent: AgreementFilterComponent;
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly permissionService = inject(PermissionService);

    public readonly align = Align;
    public sortKey: keyof InventoryResponse = 'inventoryNumber';
    public sortAscending = true;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public showFilter = true;
    public isFilterEmpty = true;
    public showHeader = true;
    public readonly tableType = TableType.Inventory;
    public filterConfig: FilterConfig = {
        fields: ['manufacturer', 'typeOfInventory', 'inventoryusers']
    };

    public numOfInventories: number = null;

    public loadingInventoryIds: string[] = [];
    public permissions = Permission;
    public userPermissions: Permission[] = this.permissionService.userPermissions;

    public includeFlags: Record<string, boolean> = {};

    private readonly sortSubject: BehaviorSubject<SortType<InventoryResponse>> = new BehaviorSubject<
        SortType<InventoryResponse>
    >({
        key: this.sortKey,
        ascending: this.sortAscending
    });
    public sort$: Observable<SortType<InventoryResponse>> = this.sortSubject.asObservable();
    private readonly searchSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public search$: Observable<void> = this.searchSubject.asObservable();
    private readonly deletedAgreementIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public deletedAgreementIds$: Observable<string[]> = this.deletedAgreementIdsSubject.asObservable();

    public get f() {
        return this.filterForm.controls;
    }

    private readonly getAgreementSubject = new BehaviorSubject<SelectedFilter>(null);
    public agreements$ = this.getAgreementSubject.pipe(
        //skip(1),
        debounceTime(250),
        switchMap(() => {
            const predicates = this.filterComponent.getPredicates();

            return merge(
                of(null),
                this.apiClientService.getInventories$(predicates).pipe(
                    map((response: GetInventoriesResponse) => {
                        return response;
                    }),
                    switchMap((inventory: InventoryResponse[]) =>
                        this.deletedAgreementIds$.pipe(
                            map((deletedAgreementIds) => inventory.filter((c) => !deletedAgreementIds.includes(c.id)))
                        )
                    ),
                    switchMap((inventories: InventoryResponse[]) =>
                        this.search$.pipe(
                            map(() => {
                                return this.filterResponse(inventories);
                            })
                        )
                    ),
                    switchMap((inventories: InventoryResponse[]) => {
                        this.numOfInventories = inventories.length;
                        return of(inventories);
                    }),
                    switchMap((inventories: InventoryResponse[]) =>
                        this.sort$.pipe(
                            switchMap((sort) => {
                                this.sortKey = sort.key;
                                this.sortAscending = sort.ascending;
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                return of(sortStrings<InventoryResponse>(inventories, sort.key, sort.ascending));
                            })
                        )
                    )
                )
            );
        })
    );

    public ngOnInit() {
        const fb = new FormBuilder();
        this.filterForm = fb.nonNullable.group(this.defaultFilter);
        this.f.searchPhrase.valueChanges.subscribe(() =>
            this.getAgreementSubject.next(this.getCurrentAgreementRequest())
        );
    }

    public hasWritePermission(agreement: InventoryResponse) {
        return this.userPermissions.includes(this.permissions.WriteAgreement) && !agreement.readOnly;
    }
    public getRowClass(agreement: InventoryResponse) {
        if (agreement.status == AgreementStatus.ReminderWarning) {
            if (agreement.daysLeftToNotice <= 30) {
                return 'reminderRedWarning';
            } else if (agreement.daysLeftToNotice <= 90) return 'reminderWarning';
        }
        return AgreementStatus[agreement.status].toString().toLowerCase();
    }

    public getTitle(agreement: InventoryResponse) {
        if (agreement.status == AgreementStatus.ReminderWarning)
            return (
                agreement.daysLeftToNotice +
                (this.translateService.instant('FEATURES.AGREEMENTS.LIST_DAYSLEFT_TITLE') as string)
            );
        return '';
    }

    public onIncludeToggle(agreementId: string, checked: boolean) {
        this.includeFlags[agreementId] = checked;
        console.log(this.includeFlags);
    }

    public onCheckedAllChange(checked: boolean) {
        console.log(checked);
        this.agreements$.subscribe((agreements) => {
            if (agreements) {
                agreements = agreements.filter((p) => !p.readOnly);
                console.log(agreements.length);
                this.resetIncludeFlags(agreements, checked);
            }
        });
    }

    public resetIncludeFlags(agreements: InventoryResponse[], value: boolean = false) {
        this.includeFlags = agreements.reduce<Record<string, boolean>>((acc, agreement) => {
            acc[agreement.id] = value;
            return acc;
        }, {});
    }

    public changeResponsible() {
        const modalRef = this.modalService.open(ChangeResponsbileModalComponent);
        const modalInstance = ModalHelper.getComponentInstance<ChangeResponsbileModalComponent>(modalRef);

        modalInstance.agreementIds = this.getSelectedAgreementIds();
        modalInstance.moved$.pipe(untilDestroyed(this)).subscribe({
            next: () => {
                this.getAgreementSubject.next(this.getCurrentAgreementRequest());
            }
        });
    }

    public confirmRemoveAgreements(event: Event) {
        event.stopPropagation();

        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);
        const selectedAgreementIds = this.getSelectedAgreementIds();

        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'TITLES.DELETE_INVENTORY',
            bodyLanguageKey: 'TEXTS.CONFIRM_DELETE_INVENTORY',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.changeDetectorRef.markForCheck();
                        return this.apiClientService.deleteAgreements$({ agreementIds: selectedAgreementIds });
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loadingInventoryIds = this.loadingInventoryIds.filter(
                        (id) => !selectedAgreementIds.includes(id)
                    );
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.DELETE_INVENTORY_SUCCESS') as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.deletedAgreementIdsSubject.next([
                        ...this.deletedAgreementIdsSubject.value,
                        ...selectedAgreementIds
                    ]);
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant('TOAST_MESSAGES.DELETE_INVENTORY_ERROR') as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public hasIncludeFlags() {
        return this.getSelectedAgreementIds().length > 0;
    }
    private getSelectedAgreementIds(): string[] {
        return Object.keys(this.includeFlags).filter((id) => this.includeFlags[id]);
    }

    private getCurrentAgreementRequest() {
        return this.getAgreementSubject.value;
    }

    public async navigateToAgreement(id: string) {
        await this.router.navigate([`${APP_ROUTES.Agreement}/details/${id}`]);
    }

    public onSortChange(sortKey: keyof InventoryResponse, sortAscending = true) {
        this.sortSubject.next({ key: sortKey, ascending: sortAscending });
    }

    public goToCreateNewAgreement() {
        void this.router.navigate([`/${APP_ROUTES.Agreement}/${AgreementRoutes.CreateNewAgreement}`], {
            queryParams: {
                agreementtypeid: 13
            }
        });
    }

    public clearFilter() {
        this.filterComponent.clearFilter();
        this.filterForm.controls.searchPhrase.setValue('');
        this.getAgreementSubject.next(this.getCurrentAgreementRequest());
    }

    public onFilterChange(filter: SelectedFilter) {
        console.log('filter changed');
        this.getAgreementSubject.next(filter);
    }

    private filterResponse(response: GetInventoriesResponse) {
        const searchPhrase = this.f.searchPhrase.value.toLowerCase();

        return response.filter((inventory) =>
            Object.values(inventory).some(
                (value) => typeof value === 'string' && value.toLowerCase().includes(searchPhrase)
            )
        );
    }
}
